export type UsageAggregate = {
  requests: number;
  totalTokens: number;
  costMicros: number;
  rawTokens: number;
  rawCostMicros: number;
  ratedTokens: number;
  ratedCostMicros: number;
};

export type UsageUserRow = UsageAggregate & {
  userId: string;
  name: string;
  email: string;
};

export type UsageModelRow = UsageAggregate & {
  modelId: string | null;
  modelName: string;
  modelRemoteId: string;
  kind: string;
  promptTokens: number;
  completionTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
};

export type UsageDailyRow = UsageAggregate & { date: string };

export type UsageData = {
  days: number;
  totals: UsageAggregate & {
    promptTokens: number;
    completionTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
  };
  byUser: UsageUserRow[];
  byModel: UsageModelRow[];
  daily: UsageDailyRow[];
};

const numberValue = (value: unknown) => {
  const result = typeof value === "number" ? value : Number(value);
  return Number.isFinite(result) && result >= 0 ? result : 0;
};

const textValue = (value: unknown) => (typeof value === "string" ? value : "");

const aggregate = (value: unknown): UsageAggregate => {
  const row = value && typeof value === "object" ? value : {};
  const source = row as Record<string, unknown>;
  return {
    requests: numberValue(source.requests),
    totalTokens: numberValue(source.totalTokens),
    costMicros: numberValue(source.costMicros),
    rawTokens: numberValue(source.rawTokens ?? source.totalTokens),
    rawCostMicros: numberValue(source.rawCostMicros ?? source.costMicros),
    ratedTokens: numberValue(source.ratedTokens),
    ratedCostMicros: numberValue(source.ratedCostMicros),
  };
};

const compareText = (left: string, right: string) =>
  left.localeCompare(right, "en", { sensitivity: "base" }) ||
  left.localeCompare(right);

export function normalizeUsage(value: unknown): UsageData {
  const source = value && typeof value === "object" ? value : {};
  const input = source as Record<string, unknown>;
  const totalsInput = input.totals;
  const totalsSource =
    totalsInput && typeof totalsInput === "object"
      ? (totalsInput as Record<string, unknown>)
      : {};
  const users = Array.isArray(input.byUser) ? input.byUser : [];
  const models = Array.isArray(input.byModel) ? input.byModel : [];
  const daily = Array.isArray(input.daily) ? input.daily : [];

  return {
    days: [7, 30, 90].includes(numberValue(input.days))
      ? numberValue(input.days)
      : 30,
    totals: {
      ...aggregate(totalsInput),
      promptTokens: numberValue(totalsSource.promptTokens),
      completionTokens: numberValue(totalsSource.completionTokens),
      cacheReadTokens: numberValue(totalsSource.cacheReadTokens),
      cacheWriteTokens: numberValue(totalsSource.cacheWriteTokens),
    },
    byUser: users
      .map((value) => {
        const row = value && typeof value === "object" ? value : {};
        const source = row as Record<string, unknown>;
        return {
          ...aggregate(value),
          userId: textValue(source.userId),
          name: textValue(source.name),
          email: textValue(source.email),
        };
      })
      .sort(
        (left, right) =>
          right.totalTokens - left.totalTokens ||
          right.costMicros - left.costMicros ||
          compareText(left.userId, right.userId),
      ),
    byModel: models
      .map((value) => {
        const row = value && typeof value === "object" ? value : {};
        const source = row as Record<string, unknown>;
        return {
          ...aggregate(value),
          modelId: typeof source.modelId === "string" ? source.modelId : null,
          modelName: textValue(source.modelName),
          modelRemoteId: textValue(source.modelRemoteId),
          kind: textValue(source.kind),
          promptTokens: numberValue(source.promptTokens),
          completionTokens: numberValue(source.completionTokens),
          cacheReadTokens: numberValue(source.cacheReadTokens),
          cacheWriteTokens: numberValue(source.cacheWriteTokens),
        };
      })
      .sort(
        (left, right) =>
          right.totalTokens - left.totalTokens ||
          right.costMicros - left.costMicros ||
          compareText(left.modelRemoteId, right.modelRemoteId) ||
          compareText(left.modelId ?? "", right.modelId ?? "") ||
          compareText(left.kind, right.kind),
      ),
    daily: daily
      .map((value) => {
        const row = value && typeof value === "object" ? value : {};
        const source = row as Record<string, unknown>;
        return { ...aggregate(value), date: textValue(source.date) };
      })
      .sort((left, right) => compareText(left.date, right.date)),
  };
}

/** Remove member identifiers and contact data before an organization export. */
export function exportableUsage(value: unknown): UsageData {
  const data = normalizeUsage(value);
  return {
    ...data,
    byUser: data.byUser.map((row) => ({
      ...row,
      userId: "",
      name: "",
      email: "",
    })),
  };
}

export function usageExportJson(value: unknown) {
  return JSON.stringify(exportableUsage(value), null, 2);
}
